import React, { FunctionComponent } from 'react';
import * as yup from 'yup';
import ApolloForm from '$/components/ApolloForm';

import { TeamFormModes } from '$/sections/Home/Profile/TeamManager/TeamManager.types';
import { RolePopup } from '$/sections/Home/Profile/TeamManager/RolePopup';
import { isEmailAlreadyExist } from '$/utils/validators/isEmailAlreadyExist';
import { useAddUserMutation, useUpdateUserMutation } from '$/GraphQL/schema/schema';
import { UserRole, EditUserInput, SocialLink, MyRole } from '$/GraphQL/schema/types';

import { TEAM_USERS_REFETCH } from '$/GraphQL/refetchQueries';
import { css } from '@emotion/react';
import { WithFormContext } from '$/components/WithFormContext';
import { ME } from '$/GraphQL/queries/Users';
import { yupIsValidUrl } from '$/utils/isUrl';
import { useMe } from '$/hooks/useMe';
import Dropdown from '$/components/ui/Input/Basics/Dropdown';
import ProfilePictureUpload from '$/components/ui/Input/ProfilePictureUpload';
import { LocalTimeZone } from '$/utils/LocalTimeZone';
import { useTimezones } from '$/utils/timeZones';
import { PureQueryOptions } from '@apollo/client';
import Modal from '$/components/ui/Modal';
import Form from '$/components/ui/Form';
import Text from '$/components/ui/Text';
import Input from '$/components/ui/Input/Basics/Input';
import Settings from '$/components/ui/Settings';
import { roleOptions } from './roleOptions';
import { PresenterInformationFields } from './PresenterInformation';
import { useLocation } from '@reach/router';
import { rolesPermissionsMatrix } from '$/shared/frontend/rolesPermissionsMatrix';

// import { DevTool } from 'react-hook-form-devtools';

interface FormProps {
  mode: TeamFormModes;
  onClose: (id?: string) => void;
  defaultValues?: Partial<EditUserInput>;
}

const setSocialLinks = (socialLinks: SocialLink | null | undefined): SocialLink | null => {
  if (!socialLinks || Object.keys(socialLinks).length === 0) {
    return null;
  }

  const ssLinks = Object.entries(socialLinks).reduce((newSocialLink: SocialLink, [key, value]) => {
    if (value) {
      if (key === 'facebook') {
        newSocialLink.facebook = value;
      }
      if (key === 'twitter') {
        newSocialLink.twitter = value;
      }
      if (key === 'linkedin') {
        newSocialLink.linkedin = value;
      }
      if (key === 'instagram') {
        newSocialLink.instagram = value;
      }
    }
    return newSocialLink;
  }, {});

  return Object.keys(ssLinks).length > 0 ? ssLinks : null;
};

const UserEditModal: FunctionComponent<FormProps> = ({
  mode,
  onClose,
  defaultValues = {
    role: UserRole.Creator,
    timezone: LocalTimeZone.name,
  },
}) => {
  const me = useMe();
  const timeZones = useTimezones();
  const [addUserToTeam] = useAddUserMutation();
  const [updateUserInTeam] = useUpdateUserMutation();
  const isAdmin = [MyRole.Admin, MyRole.Ops].includes(me.inTeam.role);
  const location = useLocation();

  const isMe = me.id === defaultValues.id;

  const validationSchema = yup.object().shape({
    firstName: yup.string().when('role', {
      is: UserRole.Presenter,
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.required(),
    }),
    lastName: yup.string().when('role', {
      is: UserRole.Presenter,
      then: (schema) => schema.nullable(),
      otherwise: (schema) => schema.required(),
    }),
    details: yup.object().when('role', {
      is: UserRole.Presenter,
      then: (schema) =>
        schema.shape({
          presenterFirstName: yup.string().required(),
          presenterLastName: yup.string().required(),
        }),
    }),
    phone: yup.string().nullable(),
    email: yup.string().when('role', {
      is: UserRole.Presenter,
      then: (schema) => schema.nullable(),
      otherwise: (schema) =>
        schema
          .trim()
          .email()
          .required()
          .test({
            name: 'emailExist',
            test: async function (value: string) {
              if (!value || (defaultValues && defaultValues.email === value)) {
                return true;
              }
              const message = `Email ${value} already registered.`;
              const exist = await isEmailAlreadyExist(value);
              return exist ? this.createError({ message: message, path: 'email' }) : true;
            },
          }),
    }),
    displayEmail: yup.string().trim().email().nullable(),
    socialLinks: yup.object().shape({
      facebook: yupIsValidUrl().nullable(),
      twitter: yupIsValidUrl().nullable(),
      linkedin: yupIsValidUrl().nullable(),
      instagram: yupIsValidUrl().nullable(),
    }),
  });

  const handleSubmit = async (values: EditUserInput): Promise<void> => {
    const data = { ...values };
    if (data.role === UserRole.Presenter) {
      data.email = mode === 'create' ? 'presenter@ewebinar.com' : defaultValues.email ?? '';
      data.firstName = data.details?.presenterFirstName ?? '';
      data.lastName = data.details?.presenterLastName ?? '';
    }

    /*
    try {
      await validationSchema.validateSync(values)
    } catch (e) {
      console.log(e);
    }
    */

    const socialLinks = data.socialLinks;
    delete data.socialLinks;

    const refetchQueries: PureQueryOptions[] = [TEAM_USERS_REFETCH];

    if (isMe) {
      refetchQueries.push({
        query: ME,
        variables: {},
      });
    }

    switch (mode) {
      case 'create': {
        const addUserToTeamResult = await addUserToTeam({
          variables: {
            data: {
              ...data,
              socialLinks: setSocialLinks(socialLinks),
              role: data.role!,
            },
          },
          refetchQueries,
        });

        if (addUserToTeamResult.data?.addUser) {
          onClose(addUserToTeamResult.data.addUser.id);
          return;
        }
        break;
      }

      default: {
        // Edit
        const updateUserInTeamResult = await updateUserInTeam({
          variables: {
            data: {
              ...data,
              socialLinks: setSocialLinks(socialLinks),
              role: data.role!,
            },
          },
          refetchQueries,
        });

        if (updateUserInTeamResult.data?.updateUser) {
          onClose(updateUserInTeamResult.data.updateUser.id);
          return;
        }
      }
    }

    throw 'Unable to save';
  };

  const roleFieldOptions = roleOptions.filter((option) => {
    if (
      mode === 'create' &&
      defaultValues.role !== UserRole.Presenter &&
      option.value === UserRole.Presenter.toString()
    ) {
      return false;
    }
    return true;
  });

  return (
    <ApolloForm
      onSubmit={handleSubmit}
      defaultValues={{
        ...defaultValues,
        email: defaultValues.role === UserRole.Presenter ? '' : defaultValues.email,
      }}
      validationSchema={validationSchema}
    >
      <WithFormContext>
        {({ watch /*, control */ }): JSX.Element => {
          const firstName = watch('firstName');
          const lastName = watch('lastName');
          const role = watch('role');
          const isPresenter = role === UserRole.Presenter;
          const isPresentersPage = location.pathname.includes(
            rolesPermissionsMatrix.settingsPresenters.path
          );
          const hideUserFields = !isAdmin || isPresenter || isPresentersPage;
          const hideRoleField = !isAdmin || isPresentersPage;

          return (
            <Modal className={`container-team-modal`}>
              {/* <DevTool control={control} /> */}
              <Modal.StickyButtonHeader>
                <Modal.Close
                  onClick={(): void => {
                    onClose(undefined);
                  }}
                />
              </Modal.StickyButtonHeader>
              <Modal.Body
                className='pt-6'
                css={css`
                  max-height: calc(74vh - 5rem);
                  @media (max-width: 768px) {
                    max-height: calc(100vh - 5rem);
                  }
                `}
              >
                <div className='w-full pl-2'>
                  <Form.Field
                    name='profileMediaRef'
                    className='mt-2'
                    component={
                      <ProfilePictureUpload
                        user={{
                          id: watch('id'),
                          firstName: firstName || '',
                          lastName: lastName || '',
                        }}
                      />
                    }
                  />
                </div>
                <div className={`w-full ${hideRoleField ? 'hidden' : isPresenter ? 'mb-2e' : ''}`}>
                  <Text className='flex pb-1'>
                    {mode === 'create' ? 'Select role of new team member' : 'Role of team member'}{' '}
                    <RolePopup />
                  </Text>
                  <Form.Field
                    name='role'
                    className={`${isMe ? 'cursor-not-allowed' : ''}`}
                    component={
                      <Dropdown
                        options={roleFieldOptions}
                        disabled={isMe}
                        placeholder='Select role'
                      />
                    }
                  />
                </div>
                <div
                  className={`flex flex-wrap justify-between items-start ${hideUserFields ? 'hidden' : ''}`}
                >
                  <div className='w-6/12 pr-3'>
                    <Text className='inline-block pb-1'>
                      First Name<span className='text-coral-1 text-xl'>*</span>
                    </Text>
                    <Form.Field name='firstName' component={<Input autoComplete='given-name' />} />
                  </div>
                  <div className='w-6/12 pl-3'>
                    <Text className='inline-block pb-1'>
                      Last Name<span className='text-coral-1 text-xl'>*</span>
                    </Text>
                    <Form.Field name='lastName' component={<Input autoComplete='family-name' />} />
                  </div>
                  <div className={`w-6/12 pr-3`}>
                    <Text className='inline-block pb-1'>
                      Login email<span className='text-coral-1 text-xl'>*</span>
                    </Text>
                    <Form.Field name='email' component={<Input autoComplete='email' />} />
                    <Form.StopNonHubspotForm />
                  </div>
                  <div className={role === UserRole.Presenter ? 'w-full' : 'w-6/12 pl-3'}>
                    <Text className='inline-block pb-1'>Timezone</Text>
                    <Form.Field
                      name='timezone'
                      component={
                        <Dropdown search options={timeZones} placeholder='Choose your timezone' />
                      }
                    />
                  </div>
                  <Settings.Separator />
                </div>
                <PresenterInformationFields />
              </Modal.Body>

              <Modal.Footer>
                <div className='flex justify-between items-center'>
                  <div className='w-auto'>
                    <Form.ErrorMessage />
                  </div>
                  <Form.SubmitButton className='px-8'>
                    {mode === 'create'
                      ? role === UserRole.Presenter
                        ? 'Create'
                        : 'Send invite'
                      : 'Save'}
                  </Form.SubmitButton>
                </div>
              </Modal.Footer>
            </Modal>
          );
        }}
      </WithFormContext>
    </ApolloForm>
  );
};

export default UserEditModal;
